import type { ReactNode } from "react";
import type { AttentionItem } from "@excellent/core";
import { ListCard } from "../list-card";
import { WorkList, type WorkRowModel } from "../work-row";

/**
 * Render identity of an attention row. Kept in lockstep (by hand) with the
 * server-side `attentionKey` in `@excellent/core` (manifest.ts) that the
 * dashboard merge sites de-dup on: `${title}-${href ?? ""}`. Inlined here (not
 * imported) so this client block stays self-contained.
 */
const attentionRowKey = (it: AttentionItem) => `${it.title}-${it.href ?? ""}`;

/**
 * Defensive de-dup: this list keys rows off content, not a stable id, and most
 * callers BUILD `items` by merging several sources — so a duplicate row would
 * crash React with "two children with the same key". Data sites de-dup before
 * slicing for correct top-N; this guarantees the renderer can never receive a
 * colliding key. Keeps the first occurrence.
 */
function dedupeAttentionRows(items: AttentionItem[]): AttentionItem[] {
  const seen = new Set<string>();
  return items.filter((it) => {
    const k = attentionRowKey(it);
    return seen.has(k) ? false : (seen.add(k), true);
  });
}

/**
 * Severity → the WorkRow "needs a person" notice. 3 = act now (danger), 2 = at
 * risk (warning); 1 (info) needs nobody, so it carries no mark at all — the
 * WorkRow rule that info/neutral get no attention. `AttentionItem` has no reason
 * string, so the label names the severity TIER, derived from the numeric
 * `severity` the item does carry ("At risk" is the notice label the shared work
 * feed already uses, src/work/rows.tsx).
 */
const SEVERITY_ATTENTION: Record<1 | 2 | 3, WorkRowModel["attention"]> = {
  3: { label: "Act now", level: "danger" },
  2: { label: "At risk", level: "warning" },
  1: undefined,
};

/**
 * AttentionItem → WorkRowModel — one mapping for both layouts. The severity dot
 * becomes the toned notice, the body rides the inline subtitle, and the row
 * keeps its href doorway.
 */
function attentionItemToRow(it: AttentionItem): WorkRowModel {
  return {
    id: attentionRowKey(it),
    title: it.title,
    subtitle: it.body,
    attention: SEVERITY_ATTENTION[(it.severity ?? 1) as 1 | 2 | 3],
    href: it.href,
  };
}

/**
 * Renders the "Needs attention" region.
 *
 * Two layouts:
 *   • `rail` (default) — the vertical right-rail card. Unchanged shape; the rows
 *     are now the canonical `WorkRow` (`docs/design/WORK-ROW-SYSTEM.md`).
 *   • `strip` — a full-width alert strip meant to sit directly beneath the
 *     KPI grid. Evidence-based: actionable triage belongs HIGH in the visual
 *     path (dashboard-research.md operational template; PRD §4.2 cognitive
 *     order orient→triage→analyze→recall), not in the lowest-attention
 *     bottom-right quadrant. The strip is **quiet when empty** — render
 *     nothing rather than an empty box (PRD §4.7 "quiet over loud").
 */
export function AttentionList({
  items,
  title = "Needs attention",
  emptyBody = "Nothing flagged. Workspace is clear.",
  layout = "rail",
  action,
}: {
  items: AttentionItem[];
  title?: string;
  emptyBody?: string;
  layout?: "rail" | "strip";
  /** Optional header action — e.g. a "View all" overflow link. */
  action?: ReactNode;
}) {
  const rows = dedupeAttentionRows(items);
  if (layout === "strip") {
    if (rows.length === 0) return null;
    // Most-urgent first, matching the manifest sort contract (severity DESC).
    const sorted = [...rows].sort(
      (a, b) => (b.severity ?? 1) - (a.severity ?? 1),
    );
    // Same canonical container as the rail layout (ListCard + WorkList) — the
    // strip just sorts severity-DESC (each high row leads with its severity-toned
    // notice) and is quiet when empty (returns null above).
    return (
      <ListCard title={title} count={rows.length} action={action}>
        <WorkList rows={sorted.map(attentionItemToRow)} gutter />
      </ListCard>
    );
  }

  return (
    <ListCard title={title} count={rows.length} empty={emptyBody} action={action}>
      <WorkList rows={rows.map(attentionItemToRow)} gutter />
    </ListCard>
  );
}
